import { DYNAMIC_PARAM_PREFIX } from '@unifyapps/carbon/no-code/const';
import type { InterfacePathNode } from '@unifyapps/defs/types/interface';
import { getNormalizedPathFromSlugOrPathNames } from './interfacePathNormalize';

type RedirectObject = {
  to: string;
  params?: Record<string, string>;
};

const DYNAMIC_PARAM_REGEX = /:([a-zA-Z0-9_-]+)/g;
const UNRESOLVED_DYNAMIC_PARAM_REGEX = /(^|\/):[a-zA-Z0-9_-]+(?:\/|$)/;

export function hasUnresolvedDynamicPathParams(path: string) {
  return UNRESOLVED_DYNAMIC_PARAM_REGEX.test(path);
}

export function resolveDynamicPath(
  path: string,
  pathParams: { key?: string; value?: unknown }[] | undefined,
) {
  if (!pathParams?.length) {
    return path;
  }

  const valueByKey = pathParams.reduce<Record<string, string>>((acc, item) => {
    if (!item?.key) {
      return acc;
    }
    const value = item.value;
    if (value === undefined || value === null || `${value}`.trim() === '') {
      return acc;
    }

    acc[item.key] = String(value);
    return acc;
  }, {});

  return path.replace(DYNAMIC_PARAM_REGEX, (segment, key) => {
    return key in valueByKey ? valueByKey[key] : segment;
  });
}

export function extractDynamicParams(path: string): string[] {
  const result: string[] = [];
  for (const segment of path.split('/')) {
    if (segment.startsWith(DYNAMIC_PARAM_PREFIX)) {
      result.push(segment.slice(1));
    }
  }
  return result;
}

function resolveParamValue(
  value: string,
  sourceParams: Record<string, string>,
): string | undefined {
  if (value.startsWith(DYNAMIC_PARAM_PREFIX)) {
    const paramName = value.slice(1);
    return sourceParams[paramName];
  }

  return value;
}

export function buildRedirectUrl(
  redirect: RedirectObject,
  sourceParams: Record<string, string>,
): string {
  const { to: targetPath, params: paramMapping } = redirect;
  const destinationParams = extractDynamicParams(targetPath);

  if (!destinationParams.length) {
    return targetPath;
  }

  if (!paramMapping || Object.keys(paramMapping).length === 0) {
    throw new Error(`Redirect path "${targetPath}" requires param mappings.`);
  }

  const replacements: Record<string, string> = {};
  const missingParams: string[] = [];

  for (const paramName of destinationParams) {
    const mappingValue = paramMapping[paramName];

    if (!mappingValue) {
      missingParams.push(paramName);
      continue;
    }

    const resolvedValue = resolveParamValue(mappingValue, sourceParams);
    if (!resolvedValue) {
      missingParams.push(paramName);
      continue;
    }

    replacements[paramName] = resolvedValue;
  }

  if (missingParams.length > 0) {
    throw new Error(
      `Redirect path "${targetPath}" is missing values for: ${missingParams.join(', ')}`,
    );
  }

  let finalUrl = targetPath;

  for (const paramName of Object.keys(replacements)) {
    const value = replacements[paramName];
    finalUrl = finalUrl.replaceAll(`${DYNAMIC_PARAM_PREFIX}${paramName}/`, `${value}/`);

    const tokenAtEnd = `${DYNAMIC_PARAM_PREFIX}${paramName}`;
    if (finalUrl.endsWith(tokenAtEnd)) {
      finalUrl = `${finalUrl.slice(0, -tokenAtEnd.length)}${value}`;
    }
  }

  return finalUrl;
}

export function getBuilderPath(
  pathOrSlug: string,
  builderValueByParamName?: Record<string, string | undefined>,
  pathNodes?: Record<string, InterfacePathNode>,
): string {
  const normalizedPath = getNormalizedPathFromSlugOrPathNames(pathOrSlug);

  if (!normalizedPath) {
    return normalizedPath;
  }

  let pathNodeBuilderBySegment: Record<string, string | undefined> | undefined;

  const lookupBuilderFromPathNodes = (segment: string): string | undefined => {
    if (!pathNodes) return undefined;
    if (pathNodeBuilderBySegment) return pathNodeBuilderBySegment[segment];

    pathNodeBuilderBySegment = {};
    for (const node of Object.values(pathNodes)) {
      if (
        node.segment.startsWith(DYNAMIC_PARAM_PREFIX) &&
        !(node.segment in pathNodeBuilderBySegment)
      ) {
        pathNodeBuilderBySegment[node.segment] = node.builderValue;
      }
    }
    return pathNodeBuilderBySegment[segment];
  };

  return normalizedPath
    .split('/')
    .map((segment) => {
      if (!segment.startsWith(DYNAMIC_PARAM_PREFIX)) {
        return segment;
      }

      const paramName = segment.slice(1);
      return builderValueByParamName?.[paramName] ?? lookupBuilderFromPathNodes(segment);
    })
    .join('/');
}
